import {
  SSP_CERTIFICATION_SKUS,
  SSP_SUBSCRIPTION_SKUS,
  FOCUS_CERTIFICATION_SKUS,
  FOCUS_SYSTEM_SKUS,
  ALL_FOCUS_SUBSCRIPTIONS_SKUS,
  RRP_CERTIFICATION_SKUS,
  RRP_SUBSCRIPTION_SKUS,
  ALL_CERTIFICATION_SKUS,
} from "@constants/prices";

export type PurchaseSource = "authenticated" | "url-params";
export type ProductCategory = "ssp" | "focus" | "rrp" | "voicePro" | "mixed";
export type PurchaseType = "certification" | "subscription" | "bundle" | "physical";

export interface PurchaseData {
  source: PurchaseSource;
  user: {
    firstName: string;
    lastName?: string;
    email: string;
    organizationName?: string;
  };
  purchase: {
    uuid?: string;
    items: { sku: string; quantity: number }[];
    accessories: Record<string, number>;
    categories: ProductCategory[];
    type: PurchaseType;
    hasPhysicalProducts: boolean;
    hasCertifications: boolean;
    hasSubscriptions: boolean;
  };
  context: {
    isIframe: boolean;
  };
}

export const detectPurchaseSource = (
  isAuthenticated: boolean,
  hasFormData: boolean
): PurchaseSource => {
  return isAuthenticated && hasFormData ? "authenticated" : "url-params";
};

export const hasSkuInCollection = (
  items: { sku: string; quantity: number }[],
  collection: string[]
): boolean => {
  return items.some((item) => collection.includes(item.sku));
};

export const hasPhysicalProducts = (
  items: { sku: string; quantity: number }[],
  selectedAddOns: Record<string, number>
): boolean => {
  const hasAddOns = Object.values(selectedAddOns).some((quantity) => quantity > 0);
  return hasSkuInCollection(items, FOCUS_SYSTEM_SKUS) || hasAddOns;
};

export const determineProductCategory = (
  items: { sku: string; quantity: number }[]
): ProductCategory => {
  const categories = [];

  if (
    hasSkuInCollection(items, [...SSP_SUBSCRIPTION_SKUS, ...SSP_CERTIFICATION_SKUS])
  ) {
    categories.push('ssp');
  }
  if (
    hasSkuInCollection(items, [
      ...ALL_FOCUS_SUBSCRIPTIONS_SKUS,
      ...FOCUS_CERTIFICATION_SKUS,
    ])
  ) {
    categories.push('focus');
  }
  if (
    hasSkuInCollection(items, [...RRP_SUBSCRIPTION_SKUS, ...RRP_CERTIFICATION_SKUS])
  ) {
    categories.push('rrp');
  }

  if (categories.length > 1) {
    console.warn(
      `Multiple categories detected: ${categories.join(', ')}. Using only '${categories[0]}'`
    );
  }

  return categories.length > 0 ? (categories[0] as ProductCategory) : 'ssp';
};


export const getPurchaseFlags = (
  items: { sku: string; quantity: number }[],
  selectedAddOns: Record<string, number>
) => {
  const hasAddOns = Object.values(selectedAddOns).some((quantity) => quantity > 0);
  const hasPhysicalProduct = hasSkuInCollection(items, FOCUS_SYSTEM_SKUS) || hasAddOns;
  const hasCertifications = hasSkuInCollection(items, ALL_CERTIFICATION_SKUS);
  const hasSubscriptions = hasSkuInCollection(items, [
    ...SSP_SUBSCRIPTION_SKUS,
    ...RRP_SUBSCRIPTION_SKUS,
    ...ALL_FOCUS_SUBSCRIPTIONS_SKUS,
  ]);

  const hasSubscriptionsOnly = Boolean(
    items.length && hasSubscriptions && !hasCertifications
  );
  const hasCertificationOnly = Boolean(items.length && !hasSubscriptions && hasCertifications);
  const hasBundlePurchase = Boolean(items.length && hasCertifications && hasSubscriptions);
  const hasTrainingOnlyOrBundle = Boolean(hasCertificationOnly || hasBundlePurchase);

  return {
    hasPhysicalProduct,
    hasCertifications,
    hasSubscriptions,
    hasSubscriptionsOnly,
    hasCertificationOnly,
    hasBundlePurchase,
    hasTrainingOnlyOrBundle,
  };
};

export const CATEGORY_MAP = {
  ssp: {
    shortTitle: "SSP",
    longTitle: "Safe and Sound Protocol (SSP)",
  },
  focus: {
    shortTitle: "ILS",
    longTitle: "Integrated Listening System (ILS)",
  },
  rrp: {
    shortTitle: "RRP",
    longTitle: "Rest and Restore Protocol (RRP)",
  },
  voicePro: {
    shortTitle: "VoicePro",
    longTitle: "VoicePro",
  },
  mixed: {
    shortTitle: "Multiple Programs",
    longTitle: "Multiple Programs",
  },
};